import React from 'react';
import Select, { components } from 'react-select';

// Multi-select whose menu stays open and shows a checkbox per option, so many
// units/blocks can be ticked in one go. "Select all" picks every option that
// matches the current search, so typing "A-" then "Select all" grabs that block.
const CheckboxOption = (props) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        tabIndex={-1}
        className="h-4 w-4 cursor-pointer accent-blue-600"
      />
      <span>{props.label}</span>
    </div>
  </components.Option>
);

const CheckboxMultiSelect = ({ options = [], value = [], onChange, isDisabled, ...rest }) => {
  const [inputValue, setInputValue] = React.useState('');

  const visibleOptions = inputValue
    ? options.filter((o) => String(o.label).toLowerCase().includes(inputValue.toLowerCase()))
    : options;
  const selectedIds = new Set(value.map((o) => o.value));
  const allVisibleSelected =
    visibleOptions.length > 0 && visibleOptions.every((o) => selectedIds.has(o.value));

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleOptions.map((o) => o.value));
      onChange(value.filter((o) => !visibleIds.has(o.value)));
    } else {
      onChange([...value, ...visibleOptions.filter((o) => !selectedIds.has(o.value))]);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-xs text-gray-600">
        <span>
          {value.length} of {options.length} selected
        </span>
        <div className="flex gap-3">
          <button
            type="button"
            className="text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={isDisabled || visibleOptions.length === 0}
            onClick={toggleAllVisible}>
            {allVisibleSelected ? 'Unselect all' : 'Select all'}
            {inputValue ? ' matching' : ''}
          </button>
          {value.length > 0 && (
            <button
              type="button"
              className="text-gray-600 hover:underline disabled:text-gray-400"
              disabled={isDisabled}
              onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </div>
      </div>
      <Select
        isMulti
        options={options}
        value={value}
        onChange={(next) => onChange(next || [])}
        inputValue={inputValue}
        onInputChange={(next, { action }) => {
          // Keep the search text after ticking an option so several matches can be ticked.
          if (action === 'input-change') setInputValue(next);
          if (action === 'menu-close') setInputValue('');
        }}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ Option: CheckboxOption }}
        isDisabled={isDisabled}
        menuPortalTarget={document.body}
        {...rest}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#eff6ff' : 'white',
            color: '#1f2937'
          }),
          valueContainer: (base) => ({ ...base, maxHeight: 120, overflowY: 'auto' }),
          ...rest.styles
        }}
      />
    </div>
  );
};

export default CheckboxMultiSelect;
